import type { UppKonvaShapeSource } from '../../../../model/upp-konva-source';
import type { UppKonvaShape, UppKonvaShapeType } from '../floor-plan-wire-types';
import type { UppFloorPlanElement } from '../../floor-plan-element';
import { barShapeBridge } from './bridge-bar';
import { billiardShapeBridge } from './bridge-billiard';
import {
  doorShapeBridge,
  stairsShapeBridge,
  windowShapeBridge,
} from './bridge-architecture';
import { footballShapeBridge } from './bridge-football';
import { genericShapeBridge } from './bridge-generic';
import { sofaShapeBridge } from './bridge-sofa';
import { tableShapeBridge } from './bridge-table';
import { vertexShapeBridge } from './bridge-vertex';
import type { FloorPlanShapeBridge } from './shape-bridge-types';
import {
  normalizeWireShapeGeometry,
  shapeDataRecord,
  shapeLabelsForBridge,
  shapeRowIdentity,
} from './shape-bridge-helpers';
import { floorPlanFiniteNumber } from '../../../geometry/floor-plan-konva-sync';

const byKind = new Map<string, FloorPlanShapeBridge>();
const byWireType = new Map<UppKonvaShapeType, FloorPlanShapeBridge>();

/**
 * Registra un puente en tiempo de carga. Uso principal: tests o extensiones internas.
 * English comment normalized.
 */
export function registerFloorPlanShapeBridge(bridge: FloorPlanShapeBridge): void {
  byKind.set(bridge.kind, bridge);
  byWireType.set(bridge.wireType, bridge);
}

function registerAllShapeBridges(): void {
  const all: readonly FloorPlanShapeBridge[] = [
    tableShapeBridge,
    vertexShapeBridge,
    barShapeBridge,
    doorShapeBridge,
    windowShapeBridge,
    stairsShapeBridge,
    sofaShapeBridge,
    genericShapeBridge,
    billiardShapeBridge,
    footballShapeBridge,
  ];
  for (const b of all) {
    registerFloorPlanShapeBridge(b);
  }
}

registerAllShapeBridges();

export function shapeSourceToWireRow(row: UppKonvaShapeSource): UppKonvaShape {
  const kind = String(row.kind).trim();
  const bridge = byKind.get(kind);
  if (!bridge) {
    throw new Error(`upp-konva: kind de figura no soportado en puente wire (${JSON.stringify(kind)})`);
  }
  const d = shapeDataRecord(row);
  const lb = shapeLabelsForBridge(row);
  const id = shapeRowIdentity(row);
  return bridge.sourceToWire({
    row,
    d,
    lb,
    id,
    x: floorPlanFiniteNumber(row.x),
    y: floorPlanFiniteNumber(row.y),
    width: floorPlanFiniteNumber(row.width),
    height: floorPlanFiniteNumber(row.height),
    rotation: floorPlanFiniteNumber(row.rotation),
  });
}

export function adapterRowToShapeSource(row: UppKonvaShape) {
  const bridge = byWireType.get(row.type);
  if (!bridge) {
    throw new Error(`upp-konva: fila wire no registrada: ${String((row as { type?: string }).type)}`);
  }
  return bridge.wireToSource(row);
}

export function wireRowToFloorPlanElement(row: UppKonvaShape, uuid: string): UppFloorPlanElement {
  const bridge = byWireType.get(row.type);
  if (!bridge) {
    throw new Error(`upp-konva: wire type no registrado: ${String((row as { type?: string }).type)}`);
  }
  return bridge.wireToElement(normalizeWireShapeGeometry(row), uuid);
}
